import React, { useCallback, useContext, useEffect, useId, useRef, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import InputErrorMessage from '@wix/da-ds/pkg/formControls/lego/InputErrorMessage';
import DescriptionEditor from '@wix/da-shared-react/pkg/DescriptionEditor';
import { EditorTiptapContentReadyHandler } from '@wix/da-shared-react/pkg/DescriptionEditor/DescriptionEditor';
import { EditorApi } from '@wix/da-editor-tiptap/pkg/components/Editor/types';
import { UseEditorOptions } from '@wix/da-editor-tiptap/pkg/hooks/useEditor';
import { getContentFromStorage } from '@wix/da-editor-tiptap/pkg/components/utils/tiptap';

interface Props {
  fieldName: string;
  label: string;
  placeholder: string;
}

const DescriptionField: React.FC<Props> = ({ fieldName, label, placeholder }) => {
  const id = useId();
  const isMobile = useContext(MobileContext);
  const { control, errors, setValue, getValues, clearErrors } = useFormContext();
  const {
    field: { value },
  } = useController({ name: fieldName, control });
  const error = errors[fieldName];

  const lazyEditorStateValueResolver = useRef<() => string>(undefined);
  const handleInitialContentReady: EditorTiptapContentReadyHandler = useCallback(
    (api: EditorApi) => {
      lazyEditorStateValueResolver.current = () => {
        return JSON.stringify(api.getStateForSave());
      };
      setValue(fieldName, lazyEditorStateValueResolver.current, { shouldDirty: false });
    },
    [fieldName, setValue]
  );

  const handleChange: UseEditorOptions['onEditorChange'] = useCallback(
    _editorState => {
      clearErrors(fieldName);
      setValue(fieldName, getValues(fieldName), { shouldDirty: true });
    },
    [clearErrors, fieldName, getValues, setValue]
  );

  const initialContent = useMemo(() => {
    const resolved = typeof value === 'function' ? value() : value;
    return typeof resolved === 'string' ? JSON.parse(resolved) : resolved;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMobile && initialContent) {
      setValue(
        fieldName,
        () =>
          JSON.stringify({
            version: '1',
            document: getContentFromStorage(initialContent),
          }),
        { shouldDirty: false }
      );
    }
  }, [fieldName, initialContent, isMobile, setValue]);

  return (
    <FormElement
      inputId={id}
      label={<InputLabelText isRequired>{label}</InputLabelText>}
      noBottomSpacing
    >
      <DescriptionEditor
        placeholder={placeholder}
        initialContent={initialContent}
        onChange={handleChange}
        onInitialContentReady={handleInitialContentReady}
        hasError={!!error}
      />
      {error && <InputErrorMessage>{error.message}</InputErrorMessage>}
    </FormElement>
  );
};
DescriptionField.displayName = 'DescriptionField';

export default DescriptionField;
